import React, { useContext } from "react";
import { observer } from "mobx-react";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  AnchorButton,
  Button,
  Menu,
  Navbar,
  NavbarGroup,
  PopoverInteractionKind,
  Position,
} from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import classNames from "classnames";
import qs from "qs";

import HeaderMenu from "./header-menu";
import { StoresContext, FEATURE_FLAGS } from "../../pages/_app";
import { SearchFilterKeys } from "../../utils/search";
import SearchSuggestionBox from "../search-suggestion-box";

type SubMenuItem = { href: string; label: string };

const SubMenu = ({ items }: { items: SubMenuItem[] }) => {
  const router = useRouter();
  const { pathname } = router;

  return (
    <Menu>
      {items.map(({ href, label }) => (
        // Can't use `MenuItem` from blueprintjs. Simple <a> is not good enough.
        // `Link` from needs to be used otherwise we get page refreshes instead of
        // next.js navigation.
        <li key={href}>
          <Link
            className={classNames("bp4-menu-item", { "bp4-selected": pathname == href })}
            role="menuitem"
            href={href}
          >
            {label}
          </Link>
        </li>
      ))}
    </Menu>
  );
};

const GENOMES_SUBMENU_ITEMS: SubMenuItem[] = [
  { href: "/genomes", label: "Complete Collection" },
  { href: "/genomes/bacteriology", label: "Bacteriology Collection" },
  { href: "/genomes/mycology", label: "Mycology Collection" },
  { href: "/genomes/protistology", label: "Protistology Collection" },
  { href: "/genomes/virology", label: "Virology Collection" },
];

const GenomesSubMenu = () => {
  return <SubMenu items={GENOMES_SUBMENU_ITEMS} />;
};

const CELL_BIO_SUBMENU_ITEMS: SubMenuItem[] = [
  { href: "/cell-bio", label: "Complete Cell Collection" },
  { href: "/cell-bio/animal-cells", label: "Animal Cell Collection" },
  { href: "/cell-bio/human-cells", label: "Human Cell Collection" },
  { href: "/cell-bio/organoids", label: "Organoids Collection" },
];

const CellBioSubMenu = () => {
  return <SubMenu items={CELL_BIO_SUBMENU_ITEMS} />;
};

const DocumentationMenu = () => {
  const items: [string, string][] = [
    [
      "https://docs.onecodex.com/en/collections/2314023-the-atcc-genome-portal",
      "Materials and Methods",
    ],
    ["https://docs.onecodex.com/en/articles/5812163-atcc-genome-portal-api-guide", "REST API"],
    [
      "https://docs.onecodex.com/en/articles/7905448-how-to-cite-the-atcc-genome-portal",
      "How to Cite the ATCC Genome Portal",
    ],
    [
      "https://docs.onecodex.com/en/articles/9917786-downloading-genomes-for-previously-purchased-atcc-strains",
      "Download Genome for Purchased Strain",
    ],
    ["https://github.com/ATCC-Bioinformatics", "Release Notes"],
    [
      "https://www.atcc.org/applications/reference-quality-data/discover-the-atcc-genome-portal#resources",
      "Explore Our Resources",
    ],
  ];
  return (
    <Menu>
      {items.map(([href, label]) => (
        <li key={href}>
          <Link className="bp4-menu-item" role="menuitem" href={href}>
            {label}
          </Link>
        </li>
      ))}
    </Menu>
  );
};

function navItemClassName(isActive: boolean): string {
  return isActive ? "nav-item nav-item--active" : "nav-item";
}

const AppHeader = () => {
  const rootStore = useContext(StoresContext);
  const username = rootStore.userStore.getUsername();
  const router = useRouter();
  const showSubscribeButton =
    !rootStore.userStore.hasActiveSubscription() &&
    router.pathname !== "/plans" &&
    router.pathname !== "/checkout-success" &&
    router.pathname !== "/checkout-in-progress" &&
    router.pathname !== "/checkout-in-review";

  const isHomeActive = router.route === "/";
  const isGenomesActive = router.route.startsWith("/genomes");
  const isCellBioActive = router.route.startsWith("/cell-bio");
  const isSeqSearchActive = router.route.startsWith("/sequence-search");
  const isProfileActive = router.route.startsWith("/profile");

  const performGenomeSearch = (key: SearchFilterKeys, value: string) => {
    // Genome search from the header is always performed on the All Genomes page.
    if (router.route === "/genomes") {
      // If we're already there, just add the filter (in case there's already other active filters).
      rootStore.genomeStore.addSearchFilter(key, value);
    } else {
      // If we're not there yet, redirect and apply our filter.
      rootStore.genomeStore.resetSearchFilters();
      const queryString = qs.stringify({ [key]: value });
      router.push({ pathname: "/genomes", query: queryString });
    }
  };

  return (
    <Navbar className="navbar">
      <a target="_blank" rel="noopener noreferrer" href="https://www.atcc.org">
        <span className="navbar-brand">
          <img src="/static/atcc/logo-highres.png" alt="ATCC logo" className="brand-logo" />
        </span>
      </a>
      <Link href="/">
        <span className={navItemClassName(isHomeActive)}>HOME</span>
      </Link>
      <Popover2
        interactionKind={PopoverInteractionKind.HOVER}
        popoverClassName="nav-item-genomes-submenu"
        minimal={true}
        position={Position.BOTTOM_LEFT}
        canEscapeKeyClose={true}
        content={<GenomesSubMenu />}
        className={navItemClassName(isGenomesActive)}
      >
        <a className="nav-item nav-header-text">
          {FEATURE_FLAGS.CellBio ? "MICROBE PRODUCTS" : "GENOMES"}
        </a>
      </Popover2>
      {FEATURE_FLAGS.CellBio ? (
        <Popover2
          interactionKind={PopoverInteractionKind.HOVER}
          popoverClassName="nav-item-cell-bio-submenu"
          minimal={true}
          position={Position.BOTTOM_LEFT}
          canEscapeKeyClose={true}
          content={<CellBioSubMenu />}
          className={navItemClassName(isCellBioActive)}
        >
          <a className="nav-item nav-header-text">CELL PRODUCTS</a>
        </Popover2>
      ) : null}
      <Link href="/sequence-search">
        <span className={navItemClassName(isSeqSearchActive)}>SEQUENCE SEARCH</span>
      </Link>
      <Popover2
        interactionKind={PopoverInteractionKind.HOVER}
        popoverClassName="nav-item-documentation-submenu"
        minimal={true}
        position={Position.BOTTOM_LEFT}
        canEscapeKeyClose={true}
        content={<DocumentationMenu />}
        className={navItemClassName(false)}
      >
        <a className="nav-item nav-header-text">DOCUMENTATION</a>
      </Popover2>
      <NavbarGroup className="navbar-genome-search">
        <div className="nav-item">
          <SearchSuggestionBox
            performSearch={performGenomeSearch}
            placeholder="Search for a genome..."
          />
        </div>
      </NavbarGroup>
      <NavbarGroup className="navbar-profile">
        {showSubscribeButton && (
          <Link href="/plans" passHref legacyBehavior>
            <AnchorButton intent="primary" className="subscribe-button">
              Become a Supporting Member
            </AnchorButton>
          </Link>
        )}
        <Popover2
          interactionKind={PopoverInteractionKind.HOVER}
          position={Position.BOTTOM_LEFT}
          canEscapeKeyClose={true}
          content={<HeaderMenu />}
          minimal={true}
          className={navItemClassName(isProfileActive)}
        >
          <Button text={username.toUpperCase()} icon="person" minimal />
        </Popover2>
      </NavbarGroup>
    </Navbar>
  );
};

export default observer(AppHeader);
